<script setup lang="ts">
import type { FormSubmitEvent } from '@primevue/forms/form';
import { CircleCheck, CircleX, LoaderCircle, Send } from 'lucide-vue-next';
import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { signUp as signUpRequest, SignUpResult } from '@api/sign-up';
import Questionnaire from './questionnaire.vue';
import RegistrationDetails, { type RegistrationDetailsModel } from './registration-details.vue';

const { t } = useI18n();

const props = defineProps<{
    countryList?: Record<string, string> | undefined;
}>();

const setPageTitle = inject<(title: string) => void>('setPageTitle');

const requestError = ref(false);
const requestErrorMessage = ref('');
const requestLoading = ref(false);
const requestSuccess = ref(false);

const INDEX = { QUESTIONNAIRE: 2, SIGN_UP: 1 };
const sectionIndex = ref(INDEX.SIGN_UP);
const registrationDetailsModel = ref<RegistrationDetailsModel>({});
const questionnaireModel = ref<string[]>([]);

const titleForState = computed(() => {
    if (requestSuccess.value || requestError.value) return '';

    switch (sectionIndex.value) {
        case INDEX.SIGN_UP:
            return t('signUp');
        case INDEX.QUESTIONNAIRE:
            return t('questionnaire.title');
        default:
            return '';
    }
});

watch(titleForState, (title) => setPageTitle?.(title), { immediate: true });

const onFormSubmit = async (event: FormSubmitEvent) => {
    if (!event.valid) return;

    if (sectionIndex.value < Math.max(...Object.values(INDEX))) {
        sectionIndex.value++;
        return;
    }

    requestError.value = false;
    requestLoading.value = true;
    requestSuccess.value = false;

    const result = await signUpRequest(
        registrationDetailsModel.value.firstName!,
        registrationDetailsModel.value.lastName!,
        registrationDetailsModel.value.email!,
        registrationDetailsModel.value.password!,
        registrationDetailsModel.value.company!,
        registrationDetailsModel.value.jobTitle!,
        registrationDetailsModel.value.country!,
        questionnaireModel.value,
        registrationDetailsModel.value.howDidYouHearAboutUs,
        registrationDetailsModel.value.howDidYouHearAboutUsOther
    );

    requestLoading.value = false;

    if (result === SignUpResult.SUCCESS || result === SignUpResult.REGISTERED_AND_ACTIVATED) {
        requestError.value = false;
        requestSuccess.value = true;
        return;
    }

    requestError.value = true;
    requestSuccess.value = false;
    requestErrorMessage.value = t('signUpFailed');

    if (result === SignUpResult.ERROR_BANNED) {
        requestErrorMessage.value = t('signUpBanned');
    }
};
</script>

<template>
    <div v-if="!requestSuccess && !requestError" class="flex flex-col gap-4">
        <div v-show="sectionIndex === INDEX.SIGN_UP" class="flex flex-col gap-4">
            <RegistrationDetails
                id="registration-details-form"
                v-model="registrationDetailsModel"
                :countryList="props.countryList"
                :disabled="requestLoading"
                :onFormSubmit="onFormSubmit" />
        </div>

        <div v-show="sectionIndex === INDEX.QUESTIONNAIRE" class="flex flex-col gap-4">
            <Questionnaire id="questionnaire-form" v-model="questionnaireModel" :onFormSubmit="onFormSubmit" />
        </div>

        <div class="flex flex-col-reverse md:flex-row gap-4 justify-between">
            <Button
                v-if="sectionIndex === INDEX.SIGN_UP"
                :as="requestLoading ? 'button' : 'a'"
                :disabled="requestLoading"
                class="w-full md:w-auto"
                href="#login"
                severity="secondary"
                size="small"
                :label="$t('backToLogin')"
                variant="text" />
            <Button
                v-else
                type="button"
                :disabled="requestLoading"
                class="w-full md:w-auto"
                severity="secondary"
                size="small"
                :label="$t('previous')"
                variant="text"
                @click="sectionIndex--" />

            <Button
                :disabled="requestLoading"
                type="submit"
                :form="sectionIndex === INDEX.SIGN_UP ? 'registration-details-form' : 'questionnaire-form'"
                :label="$t('submit')">
                <template #icon>
                    <LoaderCircle v-if="requestLoading" class="animate-spin" />
                    <Send v-else />
                </template>
            </Button>
        </div>
    </div>
    <div
        v-else-if="requestSuccess"
        class="flex flex-col items-center justify-center flex-1 h-full p-4"
        :aria-label="$t('ariaLabel.signUpSuccess')">
        <CircleCheck :size="48" class="text-green-500 dark:text-green-400 mb-4" />
        <p class="text-center">
            {{ t('signUpSuccessful') }}
        </p>
        <Button
            as="a"
            class="w-full md:w-auto mt-8"
            href="#login"
            severity="secondary"
            size="small"
            :label="$t('backToLogin')"
            variant="text" />
    </div>
    <div
        v-else
        class="flex flex-col items-center justify-center flex-1 h-full p-4"
        :aria-label="$t('ariaLabel.signUpFailure')">
        <CircleX :size="48" class="text-red-500 dark:text-red-400 mb-4" />
        <p class="text-center">
            {{ requestErrorMessage }}
        </p>
        <Button
            @click="requestError = false"
            class="w-full md:w-auto mt-8"
            severity="secondary"
            size="small"
            :label="$t('tryAgain')"
            variant="text" />
    </div>
</template>
